import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { Task } from '../../../../types/task';
import { getTaskTimeInfo } from '../../../../utils/taskSortUtils';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { CalendarDayItem } from '../common/CalendarDayItem';

interface SmartEmptyStateProps {
  currentDate: Date;
  allTasks: Task[];
  onDateChange: (date: Date) => void;
  onNewTask: () => void;
}

/**
 * 智能空状态组件
 * 分析用户任务数据，提供有用的导航建议
 */
export const SmartEmptyState: React.FC<SmartEmptyStateProps> = ({
  currentDate,
  allTasks,
  onDateChange,
  onNewTask
}) => {
  const { t } = useTranslation('v3/tasks');
  const [isDark] = useAtom(isDarkAtom);

  // 复用 CalendarDayItem 的任务计算逻辑
  const getTaskInfoForDate = (targetDate: Date) => {
    const targetDateOnly = new Date(targetDate);
    targetDateOnly.setHours(0, 0, 0, 0);

    const dayTasks = allTasks.filter(task => {
      const timeInfo = getTaskTimeInfo(task);
      if (!timeInfo.hasTime || !timeInfo.primaryTime) return false;

      const taskDate = new Date(timeInfo.primaryTime);
      taskDate.setHours(0, 0, 0, 0);

      return taskDate.getTime() === targetDateOnly.getTime();
    });

    return {
      total: dayTasks.length,
      tasks: dayTasks
    };
  };

  // 分析任务数据，生成6个日历指示器
  const calendarIndicators = useMemo(() => {
    if (!allTasks || allTasks.length === 0) {
      return [];
    }

    const currentDateOnly = new Date(currentDate);
    currentDateOnly.setHours(0, 0, 0, 0);

    // 生成前后30天的日期范围，检查哪些日期有任务
    const datesWithTasks = [];
    for (let i = -30; i <= 30; i++) {
      if (i === 0) continue; // 跳过当前日期

      const checkDate = new Date(currentDateOnly);
      checkDate.setDate(checkDate.getDate() + i);

      const taskInfo = getTaskInfoForDate(checkDate);
      if (taskInfo.total > 0) {
        datesWithTasks.push({
          date: checkDate,
          isPast: i < 0,
          isToday: false,
          taskCount: taskInfo.total
        });
      }
    }

    if (datesWithTasks.length === 0) {
      return [];
    }

    // 按日期排序
    datesWithTasks.sort((a, b) => a.date.getTime() - b.date.getTime());

    // 找到最近的过去和未来日期
    const pastDates = datesWithTasks.filter(d => d.isPast).reverse(); // 最近的在前
    const futureDates = datesWithTasks.filter(d => !d.isPast);

    // 生成最多6个指示器
    const indicators = [];
    const maxIndicators = 6;
    let leftCount = Math.min(3, pastDates.length);
    let rightCount = Math.min(3, futureDates.length);

    // 如果总数不够6个，调整分配
    const totalAvailable = pastDates.length + futureDates.length;
    if (totalAvailable < maxIndicators) {
      leftCount = pastDates.length;
      rightCount = futureDates.length;
    } else {
      // 如果一边不够3个，用另一边补充
      if (leftCount < 3 && rightCount + leftCount >= maxIndicators) {
        rightCount = maxIndicators - leftCount;
      } else if (rightCount < 3 && leftCount + rightCount >= maxIndicators) {
        leftCount = maxIndicators - rightCount;
      }
    }

    // 添加过去的日期
    for (let i = 0; i < leftCount; i++) {
      if (pastDates[i]) {
        indicators.push(pastDates[i]);
      }
    }

    // 添加未来的日期
    for (let i = 0; i < rightCount; i++) {
      if (futureDates[i]) {
        indicators.push(futureDates[i]);
      }
    }

    return indicators.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [allTasks, currentDate]);



  return (
    <div className="flex items-center justify-center py-12 min-h-[400px]">
      <div className={`text-center max-w-sm ${isDark ? 'text-ios-dark-gray2' : 'text-gray-500'}`}>
        {/* 图标 */}
        <svg
          className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-ios-dark-gray3' : 'text-gray-300'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>

        {/* 主要信息 */}
        <p className="text-sm mb-6">{t('emptyState.noTasksToday')}</p>

        {/* 日历指示器 */}
        {calendarIndicators.length > 0 ? (
          <div className="space-y-4">
            <p className={`text-xs mb-3 ${isDark ? 'text-ios-dark-gray3' : 'text-gray-400'}`}>
              {t('emptyState.viewOtherDatesOrAdd')}
            </p>
            <div className="flex justify-center gap-3 flex-wrap max-w-sm mx-auto">
              {calendarIndicators.map((indicator, index) => (
                <CalendarDayItem
                  key={indicator.date.toISOString()}
                  date={indicator.date}
                  currentDate={currentDate}
                  allTasks={allTasks}
                  onClick={onDateChange}
                  showMonth={true}
                  isCurrentMonth={true}
                  className="w-12 h-14"
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className={`text-xs ${isDark ? 'text-ios-dark-gray3' : 'text-gray-400'}`}>
              {t('emptyState.noTasksYet')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
